/**
 * The Overseer heartbeat as the owner sees it beside Autopilot: paused or not, a round running
 * or due, and the truthful last and next round times. Pure; reads brunel.autopilot_heartbeats
 * and the newest brunel.autopilot_rounds row. Unknown stays Unknown: nothing here is inferred
 * from the browser's own clock beyond comparing a recorded time with now.
 */

export const AUTOPILOT_HEARTBEAT_MINUTES = 15 as const;

export type AutopilotRoundOutcome = 'changed' | 'quiet' | 'paused' | 'failed' | 'no_host';

/** brunel.autopilot_heartbeats as a member reads it. */
export interface AutopilotHeartbeatRow {
  readonly paused: boolean;
  readonly next_round_at: string;
  readonly last_started_at: string | null;
  readonly last_finished_at: string | null;
  readonly last_outcome: AutopilotRoundOutcome | null;
}

/** The newest brunel.autopilot_rounds row. */
export interface AutopilotRoundRow {
  readonly id: string;
  readonly state: 'due' | 'running' | 'finished' | 'failed' | 'expired';
  readonly slot_at: string;
  readonly started_at: string | null;
  readonly finished_at: string | null;
  readonly outcome: AutopilotRoundOutcome | null;
}

export type AutopilotHeartbeatState = 'unavailable' | 'paused' | 'running' | 'waiting_for_host' | 'scheduled';

export interface AutopilotHeartbeatView {
  readonly state: AutopilotHeartbeatState;
  /** "Last round 10:15 · changes" / "No round yet". Null when unavailable. */
  readonly last: string | null;
  /** "Next round 10:30" / "Paused" / "Round running". Null when unavailable. */
  readonly next: string | null;
  /** One sentence for a tooltip or accessible label. */
  readonly detail: string;
}

export const AUTOPILOT_ROUND_OUTCOME_LABEL: Readonly<Record<AutopilotRoundOutcome, string>> = {
  changed: 'changes posted',
  quiet: 'nothing changed',
  paused: 'stopped by pause',
  failed: 'did not finish',
  no_host: 'no Overseer ran it',
};

function ms(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined) return null;
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : null;
}

export function autopilotHeartbeatView(input: {
  readonly heartbeat: AutopilotHeartbeatRow | null;
  readonly latestRound: AutopilotRoundRow | null;
  readonly nowMs: number;
  /** Formats a recorded instant for the owner, e.g. "10:15". */
  readonly time: (iso: string) => string;
}): AutopilotHeartbeatView {
  const { heartbeat, latestRound: round, nowMs, time } = input;
  if (heartbeat === null) {
    return { state: 'unavailable', last: null, next: null, detail: 'The Overseer heartbeat cannot be read.' };
  }
  const finishedAt = heartbeat.last_finished_at;
  const last = finishedAt === null
    ? 'No round yet'
    : `Last round ${time(finishedAt)}${heartbeat.last_outcome ? ` · ${AUTOPILOT_ROUND_OUTCOME_LABEL[heartbeat.last_outcome]}` : ''}`;
  const running = round !== null && round.state === 'running';
  const due = round !== null && round.state === 'due';

  if (heartbeat.paused) {
    return {
      state: 'paused',
      last,
      next: running ? 'Paused · running round stops before its next action' : 'Paused',
      detail: running
        ? 'The heartbeat is paused. The round already running takes no further action; no new round starts until you resume.'
        : 'The heartbeat is paused. No round starts until you resume it in Settings.',
    };
  }
  if (running) {
    const started = round.started_at;
    return {
      state: 'running',
      last,
      next: started === null ? 'Round running' : `Round running since ${time(started)}`,
      detail: 'An Overseer round is running now.',
    };
  }
  if (due) {
    return {
      state: 'waiting_for_host',
      last,
      next: `Round due ${time(round.slot_at)} · waiting for the Overseer`,
      detail: 'A round is due but no Overseer process has claimed it yet.',
    };
  }
  const nextMs = ms(heartbeat.next_round_at);
  const next = nextMs === null
    ? 'Next round Unknown'
    : nextMs <= nowMs ? 'Next round within a minute' : `Next round ${time(heartbeat.next_round_at)}`;
  return {
    state: 'scheduled',
    last,
    next,
    detail: `The Overseer runs one Autopilot round every ${AUTOPILOT_HEARTBEAT_MINUTES} minutes.`,
  };
}
